import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { FieldArray, FieldArrayRenderProps } from 'formik'
import { assoc } from 'ramda'
import { Box, Text, IconAdd, TextField, TextLinkButton } from '@machobear/design-system'

import CustomItem from './custom-item'
import { FormSection } from '../../components'
import { DetailsFormValues, CustomFeature } from '../../types'

interface CustomListProps {
  name: 'custom_features' | 'custom_utilities'
  label: string
  tooltip?: React.ReactNode
  options: CustomFeature[]
  typeOptions?: string[]
  formValues: DetailsFormValues
  defaultType?: string
  placeholder: string
}

const InputWithoutBorder = styled.div`
  width: 100%;

  input#custom_features-input + span,
  input#custom_utilities-input + span {
    box-shadow: none;
  }
`

const CustomList = ({
  name,
  label,
  tooltip,
  options = [],
  typeOptions = ['private', 'shared'],
  formValues,
  defaultType = 'private',
  placeholder
}: CustomListProps) => {
  const { t } = useTranslation()
  const [inputText, setInputText] = useState('')

  const unitScope = formValues.unit_type_scope_txt_id
  const showTypeOptions = unitScope !== 'entire'

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value)
  }, [])

  const handleAdd = useCallback(
    (arrayHelpers: FieldArrayRenderProps) => {
      if (!inputText.trim()) return
      arrayHelpers.push({
        name: inputText.trim(),
        type: defaultType
      })
      setInputText('')
    },
    [inputText, defaultType]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, arrayHelpers: FieldArrayRenderProps) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleAdd(arrayHelpers)
      }
    },
    [handleAdd]
  )

  return (
    <FormSection label={label} tooltip={tooltip}>
      <FieldArray
        name={name}
        render={arrayHelpers => (
          <Box
            display="flex"
            flexDirection="column"
            borderRadius="small"
            border="borderNeutralLight"
            role="listbox"
          >
            {options.map((option, index) => (
              <CustomItem
                key={`${option.name}-${index}`}
                option={option}
                currentType={option?.type || defaultType}
                showTypeOptions={showTypeOptions}
                typeOptions={typeOptions}
                onTypeChange={(type: string) => {
                  const newVal = assoc('type', type, options[index])
                  arrayHelpers.replace(index, newVal)
                }}
                onRemove={() => arrayHelpers.remove(index)}
              />
            ))}
            <Box
              paddingRight="small"
              display="flex"
              justifyContent="spaceBetween"
              alignItems="center"
              gap="small"
              role="group"
              aria-labelledby={`${name}-input`}
            >
              <InputWithoutBorder>
                <TextField
                  id={`${name}-input`}
                  label={placeholder}
                  value={inputText}
                  onChange={handleChange}
                  placeholder={placeholder}
                  maxLength={22}
                  actionIcon={<IconAdd title="Add" titleId="add" />}
                  onKeyDown={e => handleKeyDown(e, arrayHelpers)}
                />
              </InputWithoutBorder>
              <Box style={{ width: '120px', textAlign: 'center' }}>
                <Text>
                  <TextLinkButton
                    disabled={!inputText.trim()}
                    onClick={() => handleAdd(arrayHelpers)}
                  >
                    {t('Add', 'Add')}
                  </TextLinkButton>
                </Text>
              </Box>
            </Box>
          </Box>
        )}
      />
    </FormSection>
  )
}

export default CustomList
